import 'date-fns';
import React, { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, Button, ButtonGroup, Typography, Grid, Paper, makeStyles, Divider } from '@material-ui/core';
import { useOfflineDashboard } from 'src/contexts/OfflineDashboardContext';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';

const useStyles = makeStyles(theme => ({
    buttonCustom: {
        border: '1px solid transparent',
        '&.MuiButton-outlined': {
            borderColor: theme.palette.primary.main
        },
        '&.MuiButton-contained': {
            border: '1px solid transparent'
        }
    },
    datePickerRoot: {
        marginTop: '0px !important'
    }
}));

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

export function Overview() {
    const classes = useStyles();
    const history = useHistory();
    const query = useQuery();

    const { data, dataLoading } = useOfflineDashboard({
        dataKeys: ['WorkOrders']
    });
    const dateFormat = 'DD-MM-YYYY';

    const currentStartDate = query.get('startDate');
    const currentEndDate = query.get('endDate');

    const today = moment().startOf('day');
    const tomorrow = moment()
        .add(1, 'day')
        .startOf('day');

    const handleDateRangeFilter = (startDate, endDate) => {
        if (!startDate && !endDate) {
            query.delete('startDate');
            query.delete('endDate');
        } else {
            if (startDate) {
                query.set('startDate', startDate.format(dateFormat));
            } else {
                query.delete('startDate');
            }
            if (endDate) {
                query.set('endDate', endDate.format(dateFormat));
            } else {
                query.delete('endDate');
            }
        }
        history.replace({ search: query.toString() });
    };

    const handleSingleDateFilter = (type, date) => {
        if (!date) {
            query.delete(type);
        } else {
            query.set(type, date.format(dateFormat));
        }
        history.replace({ search: query.toString() });
    };

    const filteredWorkOrders = useMemo(() => {
        if (!data?.WorkOrders) return [];

        let filtered = data.WorkOrders;

        if (currentStartDate) {
            const startDateMoment = moment(currentStartDate, dateFormat).startOf('day');
            filtered = filtered.filter(wo => {
                if (!wo.AppointmentDate) return false;
                const appointmentDate = moment(wo.AppointmentDate).startOf('day');
                return appointmentDate.isSameOrAfter(startDateMoment, 'day');
            });
        }

        if (currentEndDate) {
            const endDateMoment = moment(currentEndDate, dateFormat).endOf('day');
            filtered = filtered.filter(wo => {
                if (!wo.AppointmentDate) return false;
                const appointmentDate = moment(wo.AppointmentDate).startOf('day');
                return appointmentDate.isSameOrBefore(endDateMoment, 'day');
            });
        }

        return filtered;
    }, [data?.WorkOrders, currentStartDate, currentEndDate]);

    if (dataLoading) return null;

    const isButtonContained = type => {
        if (type === 'all') {
            return !currentStartDate && !currentEndDate;
        }
        if (type === 'today') {
            return currentStartDate === today.format(dateFormat) && currentEndDate === today.format(dateFormat);
        }
        if (type === 'tomorrow') {
            return currentStartDate === tomorrow.format(dateFormat) && currentEndDate === tomorrow.format(dateFormat);
        }
        return false;
    };

    return (
        <Box p={2}>
            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                <Grid item>
                    <ButtonGroup variant="outlined" color="primary">
                        <Button
                            classes={{ root: classes.buttonCustom }}
                            onClick={() => handleDateRangeFilter(null, null)}
                            variant={isButtonContained('all') ? 'contained' : 'outlined'}
                        >
                            All
                        </Button>
                        <Button
                            classes={{ root: classes.buttonCustom }}
                            onClick={() => handleDateRangeFilter(today, today)}
                            variant={isButtonContained('today') ? 'contained' : 'outlined'}
                        >
                            Today
                        </Button>
                        <Button
                            classes={{ root: classes.buttonCustom }}
                            onClick={() => handleDateRangeFilter(tomorrow, tomorrow)}
                            variant={isButtonContained('tomorrow') ? 'contained' : 'outlined'}
                        >
                            Tomorrow
                        </Button>
                    </ButtonGroup>
                </Grid>
                <Grid item>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Grid container spacing={2}>
                            <Grid item>
                                <KeyboardDatePicker
                                    classes={{ root: classes.datePickerRoot }}
                                    disableToolbar
                                    variant="inline"
                                    format="dd/MM/yyyy"
                                    margin="normal"
                                    label="Start Date"
                                    value={currentStartDate ? moment(currentStartDate, dateFormat) : null}
                                    onChange={date => handleSingleDateFilter('startDate', moment(date))}
                                />
                            </Grid>
                            <Grid item>
                                <KeyboardDatePicker
                                    classes={{ root: classes.datePickerRoot }}
                                    disableToolbar
                                    variant="inline"
                                    format="dd/MM/yyyy"
                                    margin="normal"
                                    label="End Date"
                                    value={currentEndDate ? moment(currentEndDate, dateFormat) : null}
                                    onChange={date => handleSingleDateFilter('endDate', moment(date))}
                                />
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                </Grid>
            </Grid>

            <Box mt={2}>
                {filteredWorkOrders.length === 0 ? (
                    <Box py={3} px={2}>
                        <Typography component="h3" gutterBottom align="center">
                            No Work Orders Found
                        </Typography>
                    </Box>
                ) : (
                    filteredWorkOrders.map(workOrder => (
                        <Paper key={workOrder.WorkOrderId} style={{ marginBottom: 8, padding: 12 }}>
                            <Grid container spacing={2} direction="column">
                                <Grid item xs={12}>
                                    <Typography variant="h6">{workOrder.CustomerFullName}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1">
                                        Appointment Date: {moment(workOrder.AppointmentDate).format(dateFormat)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1">Receipt No: {workOrder.ReceiptNo}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body1">Status: {workOrder.Status}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => history.push(`/offline/work-order/${workOrder.WorkOrderId}`)}
                                    >
                                        View Details
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))
                )}
            </Box>
        </Box>
    );
}
